import type { SqlNullString, RoleEntity as RoleEntityType } from '../types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080';

// Decode JWT token without verification (for client-side expiration check)
function decodeJWT(token: string): { exp?: number; iat?: number } | null {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    return null;
  }
}

// Re-export types for backward compatibility
export type { SqlNullString };

// Alias for local use
export type RoleEntity = RoleEntityType;

export interface AuthResponse {
  access_token: string;
  refresh_token: string;
  roles: RoleEntity[]; // Changed from string[] to RoleEntity[]
  user: {
    id: number;
    email: string;
    name: SqlNullString | string | null; // Can be SqlNullString or plain string
    auth_provider: string;
    auth_provider_id: SqlNullString | string | null;
    created_at: string;
    updated_at: string;
  };
}

export interface UserWithRoles {
  user: AuthResponse['user'];
  roles: RoleEntity[]; // Changed from string[] to RoleEntity[]
}

// API response format (snake_case from backend)
export interface ApiRoleRequest {
  id: string;
  user_id: string;
  requested_role: string;
  reason: string;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
}

// Legacy alias for backward compatibility
export type RoleRequest = ApiRoleRequest;

export interface RoleRequestInput {
  requested_role: string;
  reason: string;
}

export interface ApiError {
  error: string;
}

export interface MessageResponse {
  message: string;
}

class ApiService {
  private baseUrl: string;
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private tokenOperationInProgress = false;
  private refreshPromise: Promise<string> | null = null;
  private refreshTimerId: ReturnType<typeof setTimeout> | null = null;
  private onAuthError: (() => void) | null = null;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
    this.loadTokensFromStorage();
  }

  setAuthErrorHandler(handler: () => void) {
    this.onAuthError = handler;
  }

  private loadTokensFromStorage() {
    try {
      this.accessToken = localStorage.getItem('accessToken');
      this.refreshToken = localStorage.getItem('refreshToken');
      
      // Set up proactive token refresh if we have tokens
      if (this.accessToken) {
        this.scheduleTokenRefresh();
      }
    } catch (error) {
      this.accessToken = null;
      this.refreshToken = null;
    }
  }

  private scheduleTokenRefresh() {
    // Clear any existing timer
    if (this.refreshTimerId) {
      clearTimeout(this.refreshTimerId);
      this.refreshTimerId = null;
    }

    if (!this.accessToken || !this.refreshToken) {
      return;
    }

    const payload = decodeJWT(this.accessToken);
    if (!payload || !payload.exp) {
      return;
    }

    const now = Math.floor(Date.now() / 1000);
    const expiresIn = payload.exp - now;

    // Refresh token 2 minutes before it expires, or immediately if already expired/expiring soon
    const refreshIn = Math.max(0, expiresIn - 120);

    this.refreshTimerId = setTimeout(async () => {
      try {
        await this.refreshAccessToken();
      } catch (error) {
        // silently ignore
      }
    }, refreshIn * 1000);
  }

  setTokens(accessToken: string, refreshToken?: string) {
    // Prevent race conditions during token operations
    if (this.tokenOperationInProgress) {
      return;
    }
    
    try {
      this.tokenOperationInProgress = true;
      this.accessToken = accessToken;
      localStorage.setItem('accessToken', accessToken);
      
      if (refreshToken) {
        this.refreshToken = refreshToken;
        localStorage.setItem('refreshToken', refreshToken);
      }
      
      // Schedule proactive token refresh
      this.scheduleTokenRefresh();
    } finally {
      this.tokenOperationInProgress = false;
    }
  }

  clearTokens() {
    // Prevent race conditions during token operations
    if (this.tokenOperationInProgress) {
      return;
    }
    
    try {
      this.tokenOperationInProgress = true;
      
      // Clear the refresh timer
      if (this.refreshTimerId) {
        clearTimeout(this.refreshTimerId);
        this.refreshTimerId = null;
      }
      
      this.accessToken = null;
      this.refreshToken = null;
      this.refreshPromise = null;
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
    } finally {
      this.tokenOperationInProgress = false;
    }
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    isRetry: boolean = false
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Copy existing headers
    if (options.headers) {
      Object.entries(options.headers).forEach(([key, value]) => {
        if (typeof value === 'string') {
          headers[key] = value;
        }
      });
    }

    // Backend uses 'Token' header (not 'Authorization')
    if (this.accessToken) {
      headers['Token'] = this.accessToken;
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      // If we get a 401 and have a refresh token, try to refresh and retry the request
      if (response.status === 401 && this.refreshToken && !isRetry && !endpoint.includes('/auth/refresh')) {
        try {
          // Use shared promise to prevent multiple simultaneous refresh attempts
          if (!this.refreshPromise) {
            this.refreshPromise = this.refreshAccessToken().finally(() => {
              this.refreshPromise = null;
            });
          }
          
          await this.refreshPromise;
          
          // Retry the original request with the new token
          return this.request<T>(endpoint, options, true);
        } catch (refreshError) {
          // Clear tokens and notify auth error handler
          this.clearTokens();
          if (this.onAuthError) {
            this.onAuthError();
          }
          throw new Error('Session expired. Please login again.');
        }
      }

      if (!response.ok) {
        const error: ApiError = await response.json().catch(() => ({
          error: `HTTP ${response.status}: ${response.statusText}`,
        }));
        
        // Include status code in error message for better error handling
        const errorMessage = `${error.error || response.statusText} (HTTP ${response.status})`;
        throw new Error(errorMessage);
      }

      return response.json();
    } catch (error) {
      throw error;
    }
  }

  // Auth endpoints
  async loginWithGoogle(googleToken: string): Promise<AuthResponse> {
    const response = await this.request<AuthResponse>('/auth/google', {
      method: 'POST',
      body: JSON.stringify({ token: googleToken }),
    });
    this.setTokens(response.access_token, response.refresh_token);
    return response;
  }

  async login(username: string, password: string): Promise<AuthResponse> {
    const response = await this.request<AuthResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
    this.setTokens(response.access_token, response.refresh_token);
    return response;
  }

  async logout(): Promise<void> {
    try {
      await this.request<MessageResponse>('/auth/logout', {
        method: 'POST',
      });
    } finally {
      this.clearTokens();
    }
  }

  async refreshAccessToken(): Promise<string> {
    if (!this.refreshToken) {
      throw new Error('No refresh token available');
    }

    const url = `${this.baseUrl}/auth/refresh`;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ refresh_token: this.refreshToken }),
      });

      if (!response.ok) {
        const error: ApiError = await response.json().catch(() => ({
          error: `HTTP ${response.status}: ${response.statusText}`,
        }));
        throw new Error(error.error || 'Token refresh failed');
      }

      const data = await response.json() as { access_token: string; refresh_token?: string };
      
      // Update tokens (some backends return a new refresh token too)
      this.setTokens(data.access_token, data.refresh_token);
      return data.access_token;
    } catch (error) {
      throw error;
    }
  }

  async getCurrentUser(): Promise<UserWithRoles> {
    return this.request<UserWithRoles>('/auth/me');
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<MessageResponse> {
    return this.request<MessageResponse>('/auth/change-password', {
      method: 'PUT',
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword,
      }),
    });
  }

  // Role request endpoints
  async submitRoleRequest(data: RoleRequestInput): Promise<{ message: string; request_id: string; status: string }> {
    return this.request('/role/request', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getRoleRequests(status?: string): Promise<RoleRequest[]> {
    const params = status ? `?status=${status}` : '';
    const res = await this.request<{ data: RoleRequest[]; message: string } | RoleRequest[]>(`/role/requests${params}`);
    // Handle both response formats: {data: [...]} or direct array
    if (res && typeof res === 'object' && 'data' in res && Array.isArray(res.data)) {
      return res.data;
    }
    return Array.isArray(res) ? res : [];
  }

  async approveRoleRequest(requestId: string): Promise<{ message: string; user_id: string; new_role: string }> {
    return this.request(`/role/requests/${requestId}/approve`, {
      method: 'POST',
    });
  }

  async rejectRoleRequest(requestId: string, reason?: string): Promise<{ message: string; request_id: string }> {
    return this.request(`/role/requests/${requestId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason: reason || '' }),
    });
  }

  // Program endpoints
  async getPrograms(): Promise<import('../types').Prodi[]> {
    const res = await this.request<{ data: import('../types').Prodi[]; message: string } | import('../types').Prodi[]>('/programs');
    // Handle both response formats: {data: [...]} or direct array
    if (res && typeof res === 'object' && 'data' in res && Array.isArray(res.data)) {
      return res.data;
    }
    return Array.isArray(res) ? res : [];
  }

  async getProgramByCode(code: string): Promise<import('../types').Prodi> {
    return this.request<import('../types').Prodi>(`/programs/${code}`);
  }

  async createProgram(data: import('../types').CreateProdiRequest): Promise<MessageResponse> {
    return this.request<MessageResponse>('/programs', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateProgram(code: string, data: import('../types').UpdateProdiRequest): Promise<MessageResponse> {
    return this.request<MessageResponse>(`/programs/${code}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteProgram(code: string): Promise<MessageResponse> {
    return this.request<MessageResponse>(`/programs/${code}`, {
      method: 'DELETE',
    });
  }

  // User Management endpoints
  async getUsers(params?: { q?: string; limit?: number; offset?: number }): Promise<import('../types').PaginatedUsersResponse> {
    const queryParams = new URLSearchParams();
    if (params?.q) queryParams.append('q', params.q);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());
    
    const query = queryParams.toString();
    return this.request<import('../types').PaginatedUsersResponse>(`/users${query ? `?${query}` : ''}`);
  }

  async getUserById(id: number): Promise<import('../types').BackendUser> {
    return this.request<import('../types').BackendUser>(`/users/${id}`);
  }

  async createUser(data: import('../types').CreateUserRequest): Promise<import('../types').BackendUser> {
    return this.request<import('../types').BackendUser>('/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateUser(id: number, data: import('../types').UpdateUserRequest): Promise<MessageResponse> {
    return this.request<MessageResponse>(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteUser(id: number): Promise<MessageResponse> {
    return this.request<MessageResponse>(`/users/${id}`, {
      method: 'DELETE',
    });
  }

  async bulkCreateUsers(data: import('../types').BulkCreateUsersRequest): Promise<MessageResponse> {
    return this.request<MessageResponse>('/users/bulk', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async bulkUpdateUsers(data: import('../types').BulkUpdateUsersRequest): Promise<MessageResponse> {
    return this.request<MessageResponse>('/users/bulk', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Student (Mahasiswa) endpoints
  async getStudents(kodeProdi?: string): Promise<import('../types').Mahasiswa[]> {
    const query = kodeProdi ? `?kode_prodi=${kodeProdi}` : '';
    const res = await this.request<{ data: import('../types').Mahasiswa[]; message: string } | import('../types').Mahasiswa[]>(`/students${query}`);
    // Handle both response formats: {data: [...]} or direct array
    if (res && typeof res === 'object' && 'data' in res && Array.isArray(res.data)) {
      return res.data;
    }
    return Array.isArray(res) ? res : [];
  }

  async getStudentByNim(nim: string): Promise<import('../types').Mahasiswa> {
    return this.request<import('../types').Mahasiswa>(`/students/${nim}`);
  }

  async createStudent(data: import('../types').CreateMahasiswaRequest): Promise<MessageResponse> {
    return this.request<MessageResponse>('/students', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateStudent(nim: string, data: import('../types').UpdateMahasiswaRequest): Promise<MessageResponse> {
    return this.request<MessageResponse>(`/students/${nim}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteStudent(nim: string): Promise<MessageResponse> {
    return this.request<MessageResponse>(`/students/${nim}`, {
      method: 'DELETE',
    });
  }

  // Lecturer (Dosen) endpoints
  async getLecturers(kodeProdi?: string): Promise<import('../types').Dosen[]> {
    const query = kodeProdi ? `?kode_prodi=${kodeProdi}` : '';
    const res = await this.request<{ data: import('../types').Dosen[]; message: string } | import('../types').Dosen[]>(`/lecturers${query}`);
    // Handle both response formats: {data: [...]} or direct array
    if (res && typeof res === 'object' && 'data' in res && Array.isArray(res.data)) {
      return res.data;
    }
    return Array.isArray(res) ? res : [];
  }

  async getLecturerByID(id: string): Promise<import('../types').Dosen> {
    return this.request<import('../types').Dosen>(`/lecturers/${id}`);
  }

  async createLecturer(data: import('../types').CreateDosenRequest): Promise<MessageResponse> {
    return this.request<MessageResponse>('/lecturers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateLecturer(id: string, data: import('../types').UpdateDosenRequest): Promise<MessageResponse> {
    return this.request<MessageResponse>(`/lecturers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteLecturer(id: string): Promise<MessageResponse> {
    return this.request<MessageResponse>(`/lecturers/${id}`, {
      method: 'DELETE',
    });
  }

  // Lecturer Assignment endpoints
  async getLecturerAssignments(params?: import('../types').GetLecturerAssignmentsParams): Promise<import('../types').DosenPengajar[]> {
    const queryParams = new URLSearchParams();
    if (params?.id_dosen) queryParams.append('id_dosen', params.id_dosen);
    if (params?.semester) queryParams.append('semester', params.semester);
    if (params?.kode_mk) queryParams.append('kode_mk', params.kode_mk);
    if (params?.id_kelas) queryParams.append('id_kelas', params.id_kelas);
    if (params?.kode_prodi) queryParams.append('kode_prodi', params.kode_prodi);
    
    const query = queryParams.toString();
    const res = await this.request<{ data: import('../types').DosenPengajar[]; message: string } | import('../types').DosenPengajar[]>(`/lecturer-assignments${query ? `?${query}` : ''}`);
    // Handle both response formats: {data: [...]} or direct array
    if (res && typeof res === 'object' && 'data' in res && Array.isArray(res.data)) {
      return res.data;
    }
    return Array.isArray(res) ? res : [];
  }

  async createLecturerAssignment(data: import('../types').CreateDosenPengajarRequest): Promise<MessageResponse> {
    return this.request<MessageResponse>('/lecturer-assignments', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateLecturerAssignment(id: number, data: import('../types').UpdateDosenPengajarRequest): Promise<MessageResponse> {
    return this.request<MessageResponse>(`/lecturer-assignments/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteLecturerAssignment(id: number): Promise<MessageResponse> {
    return this.request<MessageResponse>(`/lecturer-assignments/${id}`, {
      method: 'DELETE',
    });
  }

  // Lecturer Classes endpoints
  async getLecturerClasses(lecturerId: string, params?: { semester?: string; start_day?: string; end_day?: string }): Promise<import('../types').StudentClass[]> {
    const queryParams = new URLSearchParams();
    if (params?.semester) queryParams.append('semester', params.semester);
    if (params?.start_day) queryParams.append('start_day', params.start_day);
    if (params?.end_day) queryParams.append('end_day', params.end_day);
    
    const query = queryParams.toString();
    const res = await this.request<{ data: import('../types').StudentClass[]; message: string } | import('../types').StudentClass[]>(`/lecturers/${lecturerId}/classes${query ? `?${query}` : ''}`);
    // Handle both response formats: {data: [...]} or direct array
    if (res && typeof res === 'object' && 'data' in res && Array.isArray(res.data)) {
      return res.data;
    }
    return Array.isArray(res) ? res : [];
  }

  // Student Classes endpoints
  async getStudentClasses(nim: string, params?: { semester?: string; start_day?: string; end_day?: string }): Promise<import('../types').StudentClass[]> {
    const queryParams = new URLSearchParams();
    if (params?.semester) queryParams.append('semester', params.semester);
    if (params?.start_day) queryParams.append('start_day', params.start_day);
    if (params?.end_day) queryParams.append('end_day', params.end_day);
    
    const query = queryParams.toString();
    const res = await this.request<{ data: import('../types').StudentClass[]; message: string } | import('../types').StudentClass[]>(`/students/${nim}/classes${query ? `?${query}` : ''}`);
    // Handle both response formats: {data: [...]} or direct array
    if (res && typeof res === 'object' && 'data' in res && Array.isArray(res.data)) {
      return res.data;
    }
    return Array.isArray(res) ? res : [];
  }

  // Attendance endpoints
  async checkInAttendance(data: import('../types').StudentCheckInRequest): Promise<MessageResponse> {
    return this.request<MessageResponse>('/attendances/checkin', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCheckInAttendance(data: import('../types').StudentUpdateCheckInRequest): Promise<MessageResponse> {
    return this.request<MessageResponse>('/attendances/checkin', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async getStudentAttendances(params?: { semester?: string; kode_mk?: string }): Promise<import('../types').StudentAttendanceResponse[]> {
    const queryParams = new URLSearchParams();
    if (params?.semester) queryParams.append('semester', params.semester);
    if (params?.kode_mk) queryParams.append('kode_mk', params.kode_mk);
    
    const query = queryParams.toString();
    const res = await this.request<{ data: import('../types').StudentAttendanceResponse[]; message: string } | import('../types').StudentAttendanceResponse[]>(`/students/me/attendances${query ? `?${query}` : ''}`);
    // Handle both response formats: {data: [...]} or direct array
    if (res && typeof res === 'object' && 'data' in res && Array.isArray(res.data)) {
      return res.data;
    }
    return Array.isArray(res) ? res : [];
  }

  // Class students for a given class ID
  async getClassStudents(classId: number | string): Promise<import('../types').Mahasiswa[]> {
    const res = await this.request<{ data: import('../types').Mahasiswa[]; message: string } | null>(`/classes/${classId}/students`);
    const result = res && typeof res === 'object' && 'data' in res && Array.isArray(res.data) ? res.data : [];
    return result;
  }

  // Attendances for a given meeting (id_pertemuan)
  async getMeetingAttendances(meetingId: number): Promise<import('../types').AttendanceRecord[]> {
    const query = new URLSearchParams({ id_pertemuan: String(meetingId) }).toString();
    const res = await this.request<{ data: import('../types').AttendanceRecord[]; message: string } | import('../types').AttendanceRecord[] | null>(`/attendances?${query}`);
    // Handle both response formats: {data: [...]} or direct array
    if (res && typeof res === 'object' && 'data' in res && Array.isArray(res.data)) {
      return res.data;
    }
    return Array.isArray(res) ? res : [];
  }

  // Batch attendances for a class (all meetings) - returns AttendanceBatchResponse[]
  async getClassAttendancesBatch(classId: number): Promise<{ [meetingId: number]: import('../types').AttendanceRecord[] }> {
    const res = await this.request<{ data?: { attendances?: Record<string, import('../types').AttendanceRecord[]> } } | import('../types').AttendanceBatchResponse[] | null>(`/classes/${classId}/attendances/batch`);
    const map: { [meetingId: number]: import('../types').AttendanceRecord[] } = {};

    // New API format: { data: { attendances: { "meetingId": records[] } } }
    if (res && typeof res === 'object' && !Array.isArray(res) && 'data' in res) {
      const attendancesObj = (res as { data?: { attendances?: Record<string, import('../types').AttendanceRecord[]> } }).data?.attendances;
      if (attendancesObj && typeof attendancesObj === 'object') {
        Object.entries(attendancesObj).forEach(([meetingIdStr, records]) => {
          map[Number(meetingIdStr)] = records;
        });
      }
      return map;
    }

    // Legacy format: array of { meeting_id, attendances[] }
    if (Array.isArray(res)) {
      (res as import('../types').AttendanceBatchResponse[]).forEach((batch) => {
        map[batch.meeting_id] = batch.attendances.map((a) => ({
          id: a.id,
          id_pertemuan: a.id_pertemuan,
          nim: a.nim,
          status_kehadiran: a.status_kehadiran,
          waktu_masuk: a.waktu_masuk,
          waktu_keluar: a.waktu_keluar,
          link_bukti: a.link_bukti,
          created_at: a.created_at,
          updated_at: a.updated_at,
        }));
      });
    }

    return map;
  }

  // Bulk create/update attendance records for a class
  async bulkUpdateClassAttendances(classId: number | string, data: import('../types').BulkClassAttendanceUpdateRequest): Promise<MessageResponse> {
    return this.request<MessageResponse>(`/classes/${classId}/attendances/bulk`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Bulk update meeting schedules for a class
  async bulkUpdateClassMeetings(classId: number | string, data: import('../types').BulkMeetingUpdateRequest): Promise<MessageResponse> {
    return this.request<MessageResponse>(`/classes/${classId}/meetings/bulk`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Get full class summary (class, meetings, students, grades, lecturer assignments, attendances)
  async getClassFullSummary(classId: number | string): Promise<import('../types').ClassFullSummaryResponse> {
    return this.request<import('../types').ClassFullSummaryResponse>(`/classes/${classId}/summary`);
  }

  // Create attendance record
  async createAttendance(data: import('../types').CreateAttendanceRequest): Promise<MessageResponse> {
    return this.request<MessageResponse>('/attendances', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Update attendance record
  async updateAttendance(id: number, data: import('../types').UpdateAttendanceRequest): Promise<MessageResponse> {
    return this.request<MessageResponse>(`/attendances/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Update meeting schedule
  async updateMeeting(id: number, data: import('../types').UpdateMeetingRequest): Promise<MessageResponse> {
    return this.request<MessageResponse>(`/meetings/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Get meetings by class ID
  async getMeetingsByClass(kelasId: number): Promise<import('../types').MeetingSchedule[]> {
    const query = new URLSearchParams({ kelas_id: String(kelasId) }).toString();
    const res = await this.request<{ data: import('../types').MeetingSchedule[]; message: string } | import('../types').MeetingSchedule[] | null>(`/meetings?${query}`);
    // Handle both response formats: {data: [...]} or direct array
    if (res && typeof res === 'object' && 'data' in res && Array.isArray(res.data)) {
      return res.data;
    }
    return Array.isArray(res) ? res : [];
  }

  // Grades endpoints
  async getGrades(params?: import('../types').GetGradesParams): Promise<import('../types').GradeRecord[]> {
    const queryParams = new URLSearchParams();
    if (params?.krs_id !== undefined) queryParams.append('krs_id', String(params.krs_id));
    if (params?.kelas_id !== undefined) queryParams.append('kelas_id', String(params.kelas_id));
    const query = queryParams.toString();
    const res = await this.request<{ data: import('../types').GradeRecord[]; message: string } | import('../types').GradeRecord[] | null>(`/grades${query ? `?${query}` : ''}`);
    // Handle both response formats: {data: [...]} or direct array
    if (res && typeof res === 'object' && 'data' in res && Array.isArray(res.data)) {
      return res.data;
    }
    return Array.isArray(res) ? res : [];
  }

  async getGrade(id: number): Promise<import('../types').GradeRecord> {
    return this.request<import('../types').GradeRecord>(`/grades/${id}`);
  }

  async createGrade(data: import('../types').CreateGradeRequest): Promise<MessageResponse> {
    return this.request<MessageResponse>('/grades', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateGrade(id: number, data: import('../types').UpdateGradeRequest): Promise<MessageResponse> {
    return this.request<MessageResponse>(`/grades/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async updateClassGrades(classId: number | string, grades: import('../types').BulkGradeItem[]): Promise<MessageResponse> {
    return this.request<MessageResponse>(`/classes/${classId}/grades/bulk`, {
      method: 'PUT',
      body: JSON.stringify({ grades }),
    });
  }

  async deleteGrade(id: number): Promise<MessageResponse> {
    return this.request<MessageResponse>(`/grades/${id}`, {
      method: 'DELETE',
    });
  }

  // Enrollment endpoints
  async getEnrollments(nim: string, period: string): Promise<import('../types').EnrollmentRecord[]> {
    const res = await this.request<{ data: import('../types').EnrollmentRecord[]; message: string } | import('../types').EnrollmentRecord[] | null>(`/enrollments/${nim}/${period}`);
    // Handle both response formats: {data: [...]} or direct array
    if (res && typeof res === 'object' && 'data' in res && Array.isArray(res.data)) {
      return res.data;
    }
    return Array.isArray(res) ? res : [];
  }

  async updateEnrollments(nim: string, period: string, data: import('../types').CreateEnrollmentRequest[]): Promise<MessageResponse> {
    return this.request<MessageResponse>(`/enrollments/${nim}/${period}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // System Settings endpoints
  async getSystemSetting(key: string): Promise<{ key: string; value: string; updated_at: string }> {
    return this.request<{ key: string; value: string; updated_at: string }>(`/system-settings/${key}`);
  }

  // Course endpoints
  async getCourses(params?: import('../types').GetCoursesParams): Promise<import('../types').CourseWithClasses[]> {
    const queryParams = new URLSearchParams();
    if (params?.kode_prodi) queryParams.append('kode_prodi', params.kode_prodi);
    if (params?.include_classes !== undefined) queryParams.append('include_classes', params.include_classes.toString());
    if (params?.semester) queryParams.append('semester', params.semester);
    
    const query = queryParams.toString();
    const res = await this.request<{ data: import('../types').CourseWithClasses[]; message: string } | import('../types').CourseWithClasses[]>(`/courses${query ? `?${query}` : ''}`);
    // Handle both response formats: {data: [...]} or direct array
    if (res && typeof res === 'object' && 'data' in res && Array.isArray(res.data)) {
      return res.data;
    }
    return Array.isArray(res) ? res : [];
  }

  async getCourseByCode(code: string): Promise<import('../types').CourseWithClasses> {
    return this.request<import('../types').CourseWithClasses>(`/courses/${code}`);
  }

  // Class endpoints
  async getClassById(id: number | string): Promise<import('../types').KelasResponse> {
    const res = await this.request<{ data: import('../types').KelasResponse; message: string } | import('../types').KelasResponse>(`/classes/${id}`);
    if (res && typeof res === 'object' && 'data' in res && typeof (res as { data: unknown }).data === 'object') {
      return (res as { data: import('../types').KelasResponse }).data;
    }
    return res as import('../types').KelasResponse;
  }

  async updateClass(id: number | string, data: import('../types').UpdateKelasRequest): Promise<MessageResponse> {
    return this.request<MessageResponse>(`/classes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Efficient batch endpoints
  async getLecturerMeetingRecap(lecturerId: string, params?: { semester?: string }): Promise<import('../types').LecturerMeetingRecap> {
    const queryParams = new URLSearchParams();
    if (params?.semester) queryParams.append('semester', params.semester);
    
    const query = queryParams.toString();
    return this.request<import('../types').LecturerMeetingRecap>(`/lecturers/${lecturerId}/meeting-recap${query ? `?${query}` : ''}`);
  }

  async getClassesSummary(params: { class_ids: number[]; include_meetings?: boolean; include_attendance_stats?: boolean }): Promise<import('../types').ClassSummaryResponse[]> {
    const queryParams = new URLSearchParams();
    queryParams.append('class_ids', params.class_ids.join(','));
    if (params.include_meetings !== undefined) queryParams.append('include_meetings', params.include_meetings.toString());
    if (params.include_attendance_stats !== undefined) queryParams.append('include_attendance_stats', params.include_attendance_stats.toString());

    const response = await this.request<import('../types').BulkClassSummaryResponse>(`/classes/summary?${queryParams.toString()}`);
    return response.classes || [];
  }

  // Attendance Summary endpoints (Admin only)
  async getAttendanceSummary(params?: { semester?: string; program?: string }): Promise<import('../types').AttendanceSummaryResponse> {
    const queryParams = new URLSearchParams();
    if (params?.semester) queryParams.append('semester', params.semester);
    if (params?.program) queryParams.append('program', params.program);

    const query = queryParams.toString();
    return this.request<import('../types').AttendanceSummaryResponse>(`/admin/attendance-summary${query ? `?${query}` : ''}`);
  }

  async getAttendanceSummaryByClass(params?: { semester?: string; program?: string }): Promise<import('../types').ClassAttendanceSummary[]> {
    const queryParams = new URLSearchParams();
    if (params?.semester) queryParams.append('semester', params.semester);
    if (params?.program) queryParams.append('program', params.program);

    const query = queryParams.toString();
    return this.request<import('../types').ClassAttendanceSummary[]>(`/admin/attendance-summary/by-class${query ? `?${query}` : ''}`);
  }

  async getAttendanceSummaryByStudent(params?: { semester?: string; program?: string }): Promise<import('../types').StudentAttendanceSummary[]> {
    const queryParams = new URLSearchParams();
    if (params?.semester) queryParams.append('semester', params.semester);
    if (params?.program) queryParams.append('program', params.program);

    const query = queryParams.toString();
    return this.request<import('../types').StudentAttendanceSummary[]>(`/admin/attendance-summary/by-student${query ? `?${query}` : ''}`);
  }

  async getMeetingsBatch(params: { meeting_ids: number[]; include_attendance_count?: boolean }): Promise<import('../types').MeetingBatchResponse[]> {
    const queryParams = new URLSearchParams();
    queryParams.append('meeting_ids', params.meeting_ids.join(','));
    if (params.include_attendance_count !== undefined) queryParams.append('include_attendance_count', params.include_attendance_count.toString());
    
    const response = await this.request<import('../types').BulkMeetingBatchResponse>(`/meetings/batch?${queryParams.toString()}`);
    return response.meetings || [];
  }
}

export const apiService = new ApiService();
